import { useMemo, type FC } from 'react'
import { Label, Button, showDialog } from '@bpinternal/ui-kit-next'
import { Code, ImageUp, MousePointerClick, SquareMousePointer } from 'lucide-react'
import { Fab, generateThemeOverride, webchatClasses } from '@botpress/webchat'
import { cn } from '~/utils'
import { Input } from '~/elements'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { UploadImageDialog } from './ImageInput'
import { nanoid } from 'nanoid'
import { DEFAULT_EMBEDDED_CHAT_ID, DEFAULT_TOGGLE_CHAT_ID } from '@bpinternal/shared'
import { match } from 'ts-pattern'
import { RadioCardProps, RadioCard } from '~/elements/RadioCard'
import { RequiredVersionBadge } from './RequiredVersionBadge'
import WebchatComponentPreview from './WebchatComponentPreview'
import { useLocalStore } from '~/stores'
import { getUseConfigStore } from '../../stores/useConfigStore'

type InterfaceType = 'fab' | 'embed'
type FabType = 'default' | 'custom'

type Props = {
  embeddedChatId?: string
  toggleChatId?: string
  setEmbeddedChatId: (id: string | undefined) => void
  setToggleChatId: (id: string | undefined) => void
  fabImage?: string
  setFabImage: (image: string | undefined) => void
  workspaceId: string
  botId: string
  botAvatar?: string
}

export const ChatInterfaceForm: FC<Props> = ({
  embeddedChatId,
  toggleChatId,
  setEmbeddedChatId,
  setToggleChatId,
  fabImage,
  setFabImage,
  workspaceId,
  botId,
  botAvatar,
}) => {
  const interfaceType: InterfaceType = useMemo(
    () => (embeddedChatId !== undefined ? 'embed' : toggleChatId !== undefined ? 'fab' : 'fab'),
    [embeddedChatId, toggleChatId]
  )
  const fabType: FabType = useMemo(() => (toggleChatId !== undefined ? 'custom' : 'default'), [toggleChatId])
  const dashboardTheme = useLocalStore((state) => state.getPreferredTheme())
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])
  const variant = useConfigStore((state) => state.variant)
  const color = useConfigStore((state) => state.color)

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-1">
          <Label htmlFor="interfaceType" className="text-muted-foreground">
            Chat interface
          </Label>
        </div>
        <RadioGroupPrimitive.Root
          key={interfaceType}
          value={interfaceType}
          onValueChange={(value: InterfaceType) => {
            match(value)
              .with('embed', () => {
                if (embeddedChatId === undefined) {
                  setEmbeddedChatId(DEFAULT_EMBEDDED_CHAT_ID)
                }
                setToggleChatId(undefined)
              })
              .with('fab', () => {
                setToggleChatId(undefined)
                setEmbeddedChatId(undefined)
              })
              .exhaustive()
          }}
          className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2"
        >
          <CenteredRadioCard id="fab" value="fab" labelClassName="space-y-3">
            <div className="rounded-full bg-primary/10 p-3 group-data-[state=checked]:bg-primary">
              <MousePointerClick className="h-6 w-6 text-primary group-data-[state=checked]:text-primary-foreground" />
            </div>
            <div className="text-center">
              <div className="font-medium text-card-foreground">Toggle</div>
              <div className="mt-1 text-xs text-muted-foreground">Show and hide Webchat with a button</div>
            </div>
          </CenteredRadioCard>

          <CenteredRadioCard id="embed" value="embed">
            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="rounded-full bg-secondary/80 p-3 group-data-[state=checked]:bg-primary">
                <Code className="h-6 w-6 text-secondary-foreground group-data-[state=checked]:text-primary-foreground" />
              </div>
              <div className="text-center">
                <div className="font-medium text-card-foreground">Embedded</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Embed Webchat inside a specific element on a page
                </div>
              </div>
            </div>
            <RequiredVersionBadge version="3.3" className="absolute right-2 top-2" />
          </CenteredRadioCard>
        </RadioGroupPrimitive.Root>
      </div>

      <div>
        {interfaceType === 'embed' && (
          <div className="animate-fade-in space-y-3">
            <Label htmlFor="embeddedChatId" className="text-muted-foreground">
              Element ID
            </Label>
            <Input
              id="embeddedChatId"
              type="text"
              placeholder="e.g., chat-container"
              value={embeddedChatId}
              onChange={(e) => setEmbeddedChatId(e.target.value)}
              className="h-11 border-border bg-input/50 focus:border-primary focus:ring-primary/20"
            />
            <p className="text-sm text-muted-foreground">Enter the HTML element ID where the chat should be embedded</p>
          </div>
        )}

        {interfaceType === 'fab' && (
          <div className="animate-fade-in space-y-4">
            <Label className="text-muted-foreground">Button Style</Label>
            <RadioGroupPrimitive.Root
              value={fabType}
              onValueChange={(value: FabType) => {
                match(value)
                  .with('custom', () => {
                    if (toggleChatId === undefined) {
                      setToggleChatId(DEFAULT_TOGGLE_CHAT_ID)
                    }
                    setEmbeddedChatId(undefined)
                  })
                  .with('default', () => {
                    setEmbeddedChatId(undefined)
                    setToggleChatId(undefined)
                  })
                  .exhaustive()
              }}
              className="grid grid-cols-1 gap-4 xl:grid-cols-2"
            >
              <CenteredRadioCard id="default" value="default" labelClassName="space-y-4">
                <div
                  className="rounded-full bg-secondary/80 p-3 group-data-[state=checked]:bg-primary"
                  style={generateThemeOverride({ variant: 'solid' })}
                >
                  <div
                    className={cn(
                      webchatClasses.fab.icon.className,
                      '!size-6 scale-75 bg-primary group-data-[state=checked]:bg-primary-foreground'
                    )}
                  />
                </div>
                <div className="text-center">
                  <div className="font-medium text-card-foreground">Chat Bubble</div>
                  <div className="mt-1 text-xs text-muted-foreground">Use a customizable icon</div>
                </div>
              </CenteredRadioCard>

              <CenteredRadioCard id="custom" value="custom">
                <div className="flex flex-col items-center justify-center space-y-3">
                  <div className="rounded-full bg-secondary/80 p-3 group-data-[state=checked]:bg-primary">
                    <SquareMousePointer className="size-6 text-secondary-foreground group-data-[state=checked]:text-primary-foreground" />
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-card-foreground">Custom Element</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Use a custom button or element to open Webchat
                    </div>
                  </div>
                </div>
                <RequiredVersionBadge version="3.3" className="absolute right-2 top-2" />
              </CenteredRadioCard>
            </RadioGroupPrimitive.Root>

            {fabType === 'default' && (
              <div className="animate-slide-up mt-4 rounded-lg border border-accent-foreground/20 bg-accent/20 p-4">
                <div className="flex items-center gap-4">
                  <div className="aspect-square size-16">
                    <WebchatComponentPreview theme={{ themeMode: dashboardTheme, variant, color }} className="size-16">
                      <Fab imgUrl={fabImage} />
                    </WebchatComponentPreview>
                  </div>
                  <div>
                    <h4 className="font-medium text-accent-foreground">Chat Bubble Icon</h4>
                    <p className="mt-1 text-sm text-muted-foreground">Upload or select a custom image</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="relative flex-1"
                    onMouseDown={() =>
                      showDialog(
                        (props) => (
                          <UploadImageDialog
                            {...props}
                            workspaceId={workspaceId}
                            botId={botId}
                            imgKey={`fab-${nanoid(6)}`}
                            imgUrl={fabImage ?? ''}
                            setImgUrl={setFabImage}
                          />
                        ),
                        { title: 'Upload Image' }
                      )
                    }
                  >
                    <ImageUp className="h-4 w-4" />
                    Upload Image
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setFabImage(botAvatar)}>
                    Use bot avatar
                  </Button>
                </div>
              </div>
            )}

            {fabType === 'custom' && (
              <div className="animate-slide-up mt-4 space-y-3 rounded-lg border border-accent-foreground/20 bg-accent/20 p-4">
                <Label htmlFor="customElementId" className="text-muted-foreground">
                  Element ID
                </Label>
                <Input
                  id="customElementId"
                  type="text"
                  placeholder="e.g., open-chat-button"
                  value={toggleChatId}
                  onChange={(e) => setToggleChatId(e.target.value)}
                  className="h-10 border-border bg-input/50 focus:border-primary focus:ring-primary/20"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the ID of the HTML element that will trigger the chat when clicked
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const CenteredRadioCard: FC<RadioCardProps> = ({ labelClassName, className, ...props }) => {
  return (
    <RadioCard
      {...props}
      className={cn('flex w-full flex-1', className)}
      labelClassName={cn('flex w-full cursor-pointer flex-col items-center justify-center p-6', labelClassName)}
    />
  )
}
